if (!customElements.get("nav-overflow")) {
  customElements.define(
    "nav-overflow",
    class NavOverflow extends HTMLElement {
      connectedCallback() {
        if (this.initialized) {
          this.refresh();
          return;
        }

        this.menu = this.querySelector(".navigation-main");
        this.moreItem = this.querySelector(".navigation-item-more");
        this.moreList = this.moreItem && this.moreItem.querySelector(".navigation-more-menu");
        this.toggle = this.moreItem && this.moreItem.querySelector(".navigation-more-toggle");

        if (!this.menu || !this.moreList) {
          return;
        }

        this.items = Array.from(this.menu.children).filter((item) => item !== this.moreItem);

        if (this.items.length === 0) {
          return;
        }

        this.staticItems = new Set(
          this.items.filter((item) => item.classList.contains("navigation-item-static")),
        );
        this.widths = [];
        this.moreWidth = 0;
        this.lastWidth = null;
        this.desktop = window.matchMedia("(min-width: 990px)");
        this.refresh = this.refresh.bind(this);

        this.menu.classList.add("navigation-overflow-ready");
        this.bindToggleState();

        // Laid out synchronously so scripts measuring the header (sticky-header.js)
        // see the final single row height.
        this.layout();

        this.observer = new ResizeObserver((entries) => {
          const width = Math.round(entries[0].contentRect.width);

          if (width === this.lastWidth) {
            return;
          }

          this.lastWidth = width;
          this.refresh();
        });
        this.observer.observe(this.closest(".nav-main") || this);

        if (this.desktop.addEventListener) {
          this.desktop.addEventListener("change", this.refresh);
        }

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.refresh);
        }

        this.initialized = true;
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }

        if (this.desktop && this.desktop.removeEventListener) {
          this.desktop.removeEventListener("change", this.refresh);
        }
      }

      bindToggleState() {
        if (!this.toggle) {
          return;
        }

        const setExpanded = (state) => this.toggle.setAttribute("aria-expanded", String(state));

        this.moreItem.addEventListener("pointerenter", () => setExpanded(true));
        this.moreItem.addEventListener("pointerleave", () => setExpanded(false));
        this.moreItem.addEventListener("focusin", () => setExpanded(true));
        this.moreItem.addEventListener("focusout", (event) => {
          if (!this.moreItem.contains(event.relatedTarget)) {
            setExpanded(false);
          }
        });
      }

      refresh() {
        if (this.frame) {
          cancelAnimationFrame(this.frame);
        }

        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.layout();
        });
      }

      layout() {
        this.measure();
        this.update();
      }

      measure() {
        this.render(this.items.length);

        this.widths = this.items.map((item) => item.getBoundingClientRect().width);

        this.moreItem.hidden = false;
        this.moreWidth = this.moreItem.getBoundingClientRect().width;
        this.moreItem.hidden = true;
      }

      update() {
        if (!this.desktop.matches) {
          this.render(this.items.length);
          return;
        }

        const available = this.availableWidth();
        const total = this.widths.reduce((sum, width) => sum + width, 0);

        // Sub-pixel slack, so an item that fits exactly is never collapsed.
        const slack = 0.5;

        if (total <= available + slack) {
          this.render(this.items.length);
          return;
        }

        const budget = available - this.moreWidth;
        let used = 0;
        let visible = 0;

        for (let index = 0; index < this.items.length; index++) {
          used += this.widths[index];

          if (used > budget + slack) {
            break;
          }

          visible++;
        }

        this.render(visible);
      }

      availableWidth() {
        const nav = this.closest(".nav-main");

        if (!nav) {
          return this.clientWidth;
        }

        const navStyles = getComputedStyle(nav);
        let width =
          nav.getBoundingClientRect().width -
          parseFloat(navStyles.paddingInlineStart || 0) -
          parseFloat(navStyles.paddingInlineEnd || 0);

        Array.from(nav.children).forEach((child) => {
          if (child === this) {
            return;
          }

          const styles = getComputedStyle(child);
          width -=
            child.getBoundingClientRect().width +
            parseFloat(styles.marginInlineStart || 0) +
            parseFloat(styles.marginInlineEnd || 0);
        });

        return width;
      }

      render(count) {
        this.items.forEach((item, index) => {
          if (index < count) {
            if (item.parentElement !== this.menu) {
              item.classList.remove("navigation-item-overflowed");

              if (this.staticItems.has(item)) {
                item.classList.add("navigation-item-static");
              }

              this.menu.insertBefore(item, this.moreItem);
            }

            return;
          }

          if (item.parentElement !== this.moreList) {
            item.classList.add("navigation-item-overflowed");
            item.classList.remove("navigation-item-static", "nav-open-left");
            this.moreList.appendChild(item);
          }
        });

        this.moreItem.hidden = count >= this.items.length;

        if (!this.moreItem.hidden) {
          const edge = this.moreItem.getBoundingClientRect().left + 420;
          this.moreItem.classList.toggle("nav-open-left", edge > window.innerWidth);
        }
      }
    },
  );
}
